import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SignupModalComponent } from '../signup-modal/signup-modal.component';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AuthService } from 'src/app/shared/auth/auth.service';
import { UserService } from 'src/app/services/user.service';
import { Subject, filter, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/app/services/loader.service';
import { Business } from 'src/app/models/register-business';
import { BusinessService } from 'src/app/services/business.service';
import { TranslationService } from 'src/app/services/translation.service';
import { hasNativeLoginBridge } from 'src/app/shared/auth/auth-runtime-context';
import { PLACES_ENABLED } from 'src/app/places-routes';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
  readonly placesEnabled = PLACES_ENABLED;
  // #region INPUT, OUTPUT, VIEWCHILD
  @Input() isLogin: boolean;
  @ViewChild('signUpModal') signUpComponent!: SignupModalComponent;
  @ViewChild('loginModal') loginComponent!: LoginModalComponent;
  @ViewChild('restaurantNavigation') restaurantNavigation?: ElementRef<HTMLElement>;
  @ViewChild('restaurantMenuToggle') restaurantMenuToggle?: ElementRef<HTMLButtonElement>;
  // #endendregion
  currentLang: string;
  isMenuCollapsed = true;
  isRestaurantsMenuOpen = false;
  isRestaurantsSectionActive = false;
  getBtnInvoked: boolean = false;
  modelReference = null;
  profilePictureUrl: string;
  profilePictureName: string;
  business: Business = new Business();
  isLoginModalOpen = false;
  isFromMobileApp: boolean = false;
  loading: boolean = false;
  isWhyHungeryClicked: boolean = false;
  pathname: string;
  isOnPricingOrWhyHungery: boolean = false;
  allowArticleCreation: boolean = false;
  allowPreRegistration: boolean = false;

  private modalService = inject(NgbModal);
  callCounter: any;
  private destroy$ = new Subject<void>();
  private hasTriedTokenHydration = false;
  private isHydratingUserFromToken = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private toastService: ToastrService,
    private loaderService: LoaderService,
    private businessService: BusinessService,
    public translationService: TranslationService
  ) {
    this.currentLang = this.translationService.getCurrentLang();

    this.userService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initializeUserData();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationStart | NavigationEnd) => {
        if (event instanceof NavigationStart) {
          this.isRestaurantsMenuOpen = false;
          this.clearListingReturnWhenLeavingDetail(event.url);
          return;
        }
        if (event.url === '/login-modal' && !this.isLoginModalOpen) {
          this.openModal(this.loginComponent.loginTemplate);
          this.isLoginModalOpen = true;
        }
        this.updateRouteState(event.urlAfterRedirects || event.url);
      });

    this.authService.returnUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        if (value && !this.isLoginModalOpen) {
          this.openModal(this.loginComponent.loginTemplate);
          this.isLoginModalOpen = true;
        }
      });

    this.userService.profilePictureUrlSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.profilePictureUrl = localStorage.getItem('ProfileUrl');
      });

    this.businessService.businessData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.business.branchId = Number(localStorage.getItem('branchId'));
        this.business.name = localStorage.getItem('title');
        const cuisineNameFromStorage = localStorage.getItem('cuisineName');
        this.business.cuisineName = cuisineNameFromStorage != "null" ? cuisineNameFromStorage : '';
        const typeNameFromStorage = localStorage.getItem('typeName');
        this.business.typeName = typeNameFromStorage != "null" ? typeNameFromStorage : '';
      });
  }

  private clearListingReturnWhenLeavingDetail(nextUrl: string) {
    const currentPath = this.router.url.split('?')[0];
    const nextPath = (nextUrl || '').split('?')[0];
    const isLeavingBusinessDetail = currentPath.startsWith('/business-detail');
    const isReturningToListing = nextPath.startsWith('/listing');
    const isStayingOnBusinessDetail = nextPath.startsWith('/business-detail');

    if (isLeavingBusinessDetail && isReturningToListing) {
      this.businessService.allowListingReturnRestore();
      return;
    }

    if (isLeavingBusinessDetail && !isReturningToListing && !isStayingOnBusinessDetail) {
      this.businessService.clearListingReturnPending();
      return;
    }

    if (!isLeavingBusinessDetail && isReturningToListing) {
      this.businessService.clearListingReturnPending();
    }
  }

  private initializeUserData() {
    this.loaderService.setLoading(true);

    const user = this.userService.getUser();
    if (!user || !user.email) {
      if (this.hydrateUserFromStoredToken()) {
        return;
      }
      this.loaderService.setLoading(false);
      return;
    }

    this.isLogin = true;
    this.allowArticleCreation = user.allowArticleCreation;
    this.allowPreRegistration = user.allowPreRegistration;
    this.profilePictureUrl = localStorage.getItem('ProfileUrl');

    if (user.isMembershipVendor) {
      this.businessService.getBusiness()
        .pipe(takeUntil(this.destroy$))
        .subscribe((response: any) => {
          this.profilePictureUrl = localStorage.getItem('ProfileUrl');
          this.business = response;

          localStorage.setItem('branchId', this.business.branchId.toString());
          localStorage.setItem('title', this.business.name);

          if (this.business.cuisineName != null) {
            localStorage.setItem('cuisineName', response.cuisineName);
          }
          if (this.business.typeName != null) {
            localStorage.setItem('typeName', response.typeName);
          }
          this.loaderService.setLoading(false);
        });
    } else {
      this.loaderService.setLoading(false);
    }
    this.profilePictureUrl = localStorage.getItem('ProfileUrl');
  }

  private hydrateUserFromStoredToken(): boolean {
    if (!this.authService.getToken() || this.hasTriedTokenHydration || this.isHydratingUserFromToken) {
      return false;
    }

    this.hasTriedTokenHydration = true;
    this.isHydratingUserFromToken = true;

    this.userService.getPersonalDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (user?.profilePicture) {
            localStorage.setItem('ProfilePictureName', user.profilePicture);
            this.userService.getProfilePicture(user.profilePicture)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (profileUrl) => {
                  this.userService.setProfilePictureUrl(profileUrl);
                  this.profilePictureUrl = profileUrl;
                },
                error: () => {
                  this.profilePictureUrl = localStorage.getItem('ProfileUrl');
                }
              });
          }

          this.userService.updateUser(user);
        },
        error: () => {
          this.isHydratingUserFromToken = false;
          this.loaderService.setLoading(false);
        },
        complete: () => {
          this.isHydratingUserFromToken = false;
        }
      });

    return true;
  }

  ngOnInit() {
    this.pathname = window.location.pathname;
    this.updateRouteState(this.router.url);
    this.completeSocialRedirectLogin();

    if (['/pricing', '/why-hungery'].includes(this.pathname)) {
      this.toggleNavMenu();
    }

    this.isFromMobileApp = this.isMobileAppContext();

    document.addEventListener('scroll', this.handleScroll, true);

  }

  private isMobileAppContext(): boolean {
    // "Mobile View" means the Flutter WebView, identified only by its injected bridge.
    // A phone-sized Chrome/Safari tab is a mobile browser and must stay in the web flow.
    return this.isFlutterBridgeAvailable();
  }

  private isFlutterBridgeAvailable(): boolean {
    return hasNativeLoginBridge(window as any);
  }

  openSignInFromHeader(event?: Event) {
    if (this.openFlutterAuth('signIn', event)) {
      return;
    }
    this.openModal(this.loginComponent.loginTemplate);
    this.removeOverlay();
  }

  async openRegisterFromHeader(event?: Event) {
    const win = window as any;
    if (win.SignUpHandler?.postMessage) {
      const isAllowedCountry = await this.userService.getUserCountry();
      if (!isAllowedCountry) {
        this.showRegistrationUnavailableMessage();
        return;
      }
      this.openFlutterAuth('signUp', event);
      return;
    }
    this.signUpComponent.prepareRegistrationFlow();
    this.openModal(this.signUpComponent.signupTemplate, 'sign-in-modal');
    this.removeOverlay();
  }

  private openFlutterAuth(type: 'signIn' | 'signUp', event?: Event): boolean {
    const win = window as any;
    const handler = type === 'signIn' ? win.SignInHandler : win.SignUpHandler;
    if (!handler?.postMessage) {
      return false;
    }
    event?.preventDefault();
    event?.stopPropagation();
    (event as any)?.stopImmediatePropagation?.();
    handler.postMessage(type);
    return true;
  }

  private async completeSocialRedirectLogin() {
    const redirectResult = await this.authService.getRedirectSocialLoginResult();
    if (!redirectResult) {
      return;
    }

    this.loaderService.setLoading(true);
    this.authService.sendTokenToBackend(redirectResult.providerToken, redirectResult.loginType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (res: any) => {
          if (res && res.access_token) {
            localStorage.setItem('token', res.access_token);
            localStorage.setItem('refreshToken', res.refresh_token);
            localStorage.setItem('expiresIn', res.expires_in);

            const user = await this.userService.getPersonalDetails().toPromise();
            this.userService.updateUser(user);

            if (user?.profilePicture) {
              localStorage.setItem('ProfilePictureName', user.profilePicture);
              const img = await this.userService.getProfilePicture(user.profilePicture).toPromise();
              this.userService.setProfilePictureUrl(img);
              this.profilePictureUrl = img;
            }

            this.toastService.success('Login successful!');
            const returnUrl = this.authService.consumeSocialLoginReturnUrl();
            if (returnUrl && returnUrl !== this.router.url && returnUrl !== '/login-modal') {
              this.router.navigateByUrl(returnUrl);
            }
          } else {
            this.toastService.info("Something Went Wrong. Please try after sometimes and apologize for any inconvenience.");
          }
        },
        error: () => {
          this.toastService.info("Something Went Wrong. Please try after sometimes and apologize for any inconvenience.");
          this.loaderService.setLoading(false);
        },
        complete: () => {
          this.loaderService.setLoading(false);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && this.isLogin) {
      this.profilePictureUrl = localStorage.getItem('ProfileUrl');
      this.profilePictureName = localStorage.getItem('ProfilePictureName');
      this.business.branchId = Number(localStorage.getItem('branchId'));
      this.business.name = localStorage.getItem('title');
      const cuisineNameFromStorage = localStorage.getItem('cuisineName');
      this.business.cuisineName = cuisineNameFromStorage != "null" ? cuisineNameFromStorage : '';
      const typeNameFromStorage = localStorage.getItem('typeName');
      this.business.typeName = typeNameFromStorage != "null" ? typeNameFromStorage : '';
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollPosition =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    if (scrollPosition > 40) {
      document.body.classList.add('page-scrolled');
    } else {
      document.body.classList.remove('page-scrolled');
    }

    this.closeDropdowns();
  }

  toggleSidebar() {
    const body = document.body;
    body.classList.add('show-sidebar');
  }

  openRestaurantsMenuOnHover(event: PointerEvent) {
    if (event.pointerType === 'mouse') {
      this.isRestaurantsMenuOpen = true;
    }
  }

  closeRestaurantsMenuOnPointerLeave(event: PointerEvent) {
    // Touch release fires pointerleave before click in WebKit; keep the link visible for activation.
    if (event.pointerType !== 'mouse') return;
    if (!this.restaurantNavigation?.nativeElement.contains(document.activeElement)) {
      this.isRestaurantsMenuOpen = false;
    }
  }

  onRestaurantsMenuFocusOut(event: FocusEvent) {
    if (!this.restaurantNavigation?.nativeElement.contains(event.relatedTarget as Node)) {
      this.isRestaurantsMenuOpen = false;
    }
  }

  closeRestaurantsMenuWithEscape(event: Event) {
    if (this.isRestaurantsMenuOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.isRestaurantsMenuOpen = false;
      this.restaurantMenuToggle?.nativeElement.focus();
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  closeRestaurantsMenuOnOutsideClick(event: PointerEvent) {
    if (!this.restaurantNavigation?.nativeElement.contains(event.target as Node)) {
      this.isRestaurantsMenuOpen = false;
    }
  }

  onDiscoveryNavigation(event: MouseEvent) {
    this.isRestaurantsMenuOpen = false;
    if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
      document.body.classList.remove('show-sidebar');
    }
  }

  removeOverlay(isGetBtn = false, stickeySideBar?) {
    event.preventDefault();
    const body = document.body;
    if (!stickeySideBar) { body.classList.remove('show-sidebar'); }
    if (isGetBtn) this.getBtnInvoked = true;
  }

  handleUpdate(newValue: boolean) {
    this.getBtnInvoked = newValue;
  }

  openModal(content: TemplateRef<any>, windowClass: string = '') {
    if (windowClass == '' && this.loginComponent) {
      this.loginComponent.fromReset();
      this.modelReference = this.modalService.open(content, {
        windowClass: `project-modal ${windowClass}`,
        centered: true,
        backdropClass: 'primary-backdrop',
      });
    }
    else {
      this.userService.getUserCountry().then(
        (isAllowedCountry) => {
          if (isAllowedCountry) {
            this.modelReference = this.modalService.open(content, {
              windowClass: `project-modal ${windowClass}`,
              centered: true,
              backdropClass: 'primary-backdrop',
            });
          }
          else {
            this.showRegistrationUnavailableMessage();
          }
        }
      );
    }
  }

  private showRegistrationUnavailableMessage() {
    this.toastService.info("We're sorry, but registration is currently not available in your region. As a result, you will not be able to create an account at this time. We appreciate your interest and apologize for any inconvenience.");
  }

  onLogOutClick() {
    this.toggleNavMenu();
    this.authService.logout();
    this.router.navigate(['/']);
  }

  closeModal(e?: any) {
    if (e) {
      this.profilePictureUrl = e;
    }
    this.modelReference.close();
  }

  changeLanguage(language: string): void {
    this.translationService.changeLanguage(language);
    this.currentLang = language;
    this.translationService.changeLang(language);
  }

  private updateRouteState(url: string) {
    const path = url.split(/[?#]/)[0];
    this.isRestaurantsSectionActive = path === '/listing' || path.startsWith('/listing/') ||
      path === '/places' || path.startsWith('/places/');
    this.isOnPricingOrWhyHungery = url === '/pricing' || url === '/why-hungery';
    this.isWhyHungeryClicked = this.isOnPricingOrWhyHungery;
  }

  toggleNavMenu() {
    this.isWhyHungeryClicked = !(this.router.url === '/pricing' || this.router.url === '/why-hungery');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    // Remove scroll event listener
    document.removeEventListener('scroll', this.handleScroll, true);
  }

  // Function to close dropdowns
  private closeDropdowns() {
    this.isRestaurantsMenuOpen = false;
    const dropdowns = document.querySelectorAll('.dropdown.show');
    dropdowns.forEach(dropdown => {
      const dropdownToggle = dropdown.querySelector('[ngbDropdownToggle]');
      if (dropdownToggle) {
        (dropdownToggle as HTMLElement).click();
      }
    });
  }

  // Handler for scroll events from any element
  private handleScroll = (event: Event) => {
    if (event.target instanceof Element) {
      // Scrolling the location results is part of editing, not page navigation.
      // Clicking its filter toggle here would also dismiss the location field.
      if (event.target.closest('.mobile-location-dock')) return;
      this.closeDropdowns();
    }
  }

  whyHungeryClick(event: any) {
    if (event.ctrlKey) {
      return;
    } else {
      event.preventDefault();
      this.removeOverlay();
      this.toggleNavMenu();
    }
  }
}
